import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ToastrService } from 'ngx-toastr';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../auth/auth.service';
import { UserService } from './user.service';
import { Subject, Observable } from 'rxjs';
import { utils } from 'protractor';
import * as cryptoJS from 'crypto-js';

export const apiNotifier = new Subject();

@Injectable({
  providedIn: 'root'
})
export class ApiService {

  error: any = {};
  authorization: any;
  apiHeaders: any;
  categories: any;
  // Guards against a burst of concurrent 401/403 responses (e.g. every in-flight
  // request failing at once when the session is invalidated by a login on another
  // device) each independently triggering logout() + a toast.
  private sessionConflictHandled = false;

  constructor(
    private http: HttpClient,
    private router: Router,
    private toasterService: ToastrService,
    private authService: AuthService,
    private userService: UserService,
    private route: ActivatedRoute
  ) {
  }

   encryption(user) {
      return cryptoJS.AES.encrypt(JSON.stringify(user), environment.encryption_key);
    }

  // Stable per-browser id so guests get "recent searches" too. Only sent to the
  // search API (its CORS allows X-DEVICE-ID); the PHP APIs would fail preflight.
  deviceId(): string {
    let id = localStorage.getItem('deviceId');
    if (!id) {
      const rnd = new Uint8Array(16);
      if (window.crypto && window.crypto.getRandomValues) {
        window.crypto.getRandomValues(rnd);
      } else {
        for (let i = 0; i < 16; i++) { rnd[i] = Math.floor(Math.random() * 256); }
      }
      id = Array.from(rnd, b => ('0' + b.toString(16)).slice(-2)).join('');
      localStorage.setItem('deviceId', id);
    }
    return id;
  }

  private isSearchApi(baseServiceUrl?: string): boolean {
    return !!baseServiceUrl && baseServiceUrl === environment.searchApiUrl;
  }

  headers = (withDeviceId = false) => {
    this.authorization = this.authService.decryption();
    this.apiHeaders = new HttpHeaders();
    this.apiHeaders.headers['Accept'] = 'application/json';
    this.apiHeaders.headers['X-REQUEST-TYPE'] = 'web';
    const accessKey = localStorage.getItem('selectedAccessFlag');
    this.apiHeaders.headers['X-REQUEST-ACCESS-KEY'] = accessKey ? accessKey : 'kn';
    if (withDeviceId) {
      this.apiHeaders.headers['X-DEVICE-ID'] = this.deviceId();
    }
    if (localStorage.getItem('currentLanguage')) {
      this.apiHeaders.headers['X-LANGUAGE-CODE'] = localStorage.getItem('currentLanguage');
    } else {
      this.apiHeaders.headers['X-LANGUAGE-CODE'] = 'en';
    }

    
    this.route.queryParamMap.subscribe(params => {
      const paramData = params.get('token');
      if (paramData) {
        const user = {
          'response': {
            'name':'', 
            "is_subscribed":1,
            "tag1": 1,
            "tag2": 1,
            "tag3": 1,
            "is_adviser": 1,
          },
        } 
        const userData = this.encryption(user);
        this.userService.userData(userData);
    
        // Use the token from query parameters
        this.apiHeaders.headers['Authorization'] = 'Bearer ' + paramData;
      } else if (this.authorization && this.authorization.response && this.authorization.response.access_token) {
        // Fallback to using the token from authorization
        this.apiHeaders.headers['Authorization'] = 'Bearer ' + this.authorization.response.access_token;
      }
    });

    return this.apiHeaders;
  }


  callGetAPI(url: string, baseServiceUrl?: string) {
    const serviceUrl = (baseServiceUrl) ? baseServiceUrl + url : environment.apiUrl + url;
    return this.http.get<any>(serviceUrl, this.headers(this.isSearchApi(baseServiceUrl)))
      .pipe(map(
        data => {
          return data;
        }
      ));
  }
  callPostAPI(url: string, parameters: any, baseServiceUrl?: string) {
    const serviceUrl = (baseServiceUrl) ? baseServiceUrl + url : environment.apiUrl + url;
    return this.http.post<any>(serviceUrl, parameters, this.headers(this.isSearchApi(baseServiceUrl)))
      .pipe(map(
        data => {
          return data;
        }
      ));
  }
  // Fire-and-forget POST for reporting during page unload (beforeunload /
  // pagehide). HttpClient XHRs are cancelled by the browser once the page
  // starts tearing down; fetch with keepalive is allowed to outlive the page.
  callUnloadSafePostAPI(url: string, parameters: any, baseServiceUrl?: string) {
    const serviceUrl = (baseServiceUrl) ? baseServiceUrl + url : environment.apiUrl + url;
    const headers: any = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'X-REQUEST-TYPE': 'web',
      'X-REQUEST-ACCESS-KEY': localStorage.getItem('selectedAccessFlag') || 'kn',
      'X-LANGUAGE-CODE': localStorage.getItem('currentLanguage') || 'en'
    };
    const auth = this.authService.decryption();
    if (auth && auth.response && auth.response.access_token) {
      headers['Authorization'] = 'Bearer ' + auth.response.access_token;
    }
    try {
      fetch(serviceUrl, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(parameters),
        keepalive: true
      } as any).catch(() => {});
    } catch (e) {
      // fetch/keepalive unsupported — best effort, nothing else can run here
    }
  }

  callPutAPI(url: string, parameters: any,  baseServiceUrl?: string) {
    const serviceUrl = (baseServiceUrl) ? baseServiceUrl + url : environment.apiUrl + url;
    return this.http.put<any>(serviceUrl, parameters, this.headers())
      .pipe(map(
        data => {
          return data;
        }
      ));
  }
  callDeleteAPI(url: string, baseServiceUrl?: string) {
    const serviceUrl = (baseServiceUrl) ? baseServiceUrl + url : environment.apiUrl + url;
    return this.http.delete<any>(serviceUrl, this.headers(this.isSearchApi(baseServiceUrl)))
      .pipe(map(
        data => {
          return data;
        }
      ));
  }

  callGetJsonFile(url: string) {
    return this.http.get<any>(url)
      .pipe(map(
        data => {
          return data;
        }
      ));
  }
  successResponse(data, url = null) {
    if (data.statusCode === 200 && data.error === false) {
      this.toasterService.success('', data.message);
      if (url) {
        this.router.navigate([url.redirect]);
      }
    }
  }

  failureResponse(data, url = null) {
    this.error = {};
    if (!data || !data.error) {
      // Network failure, CORS error, timeout, or anything without a structured
      // API error body. Surface it quietly and let the current page keep loading
      // instead of navigating away.
      this.error.commonError = (data && data.message) ? data.message : 'Something went wrong. Please try again.';
      return this.error;
    }
    if (data.error.statusCode === 422 || data.error.statusCode === 500) {
      if (typeof data.error.message === 'string' || data.error.message instanceof String) {
        this.error.commonError = data.error.message;
      } else {
        this.error = data.error.message;
      }
    } else if (data.error.statusCode === 401) {
      if (!this.sessionConflictHandled) {
        this.sessionConflictHandled = true;
        this.authService.logout(false);
        this.userService.displayPopup('sign-in');
        setTimeout(() => { this.sessionConflictHandled = false; }, 5000);
      }
    } else if (data.error.statusCode === 403) {
      if (!this.sessionConflictHandled) {
        this.sessionConflictHandled = true;
        this.authService.logout(false);
        if (data.error.message && data.error.message !== '') {
          this.toasterService.error('', data.error.message);
        }
        setTimeout(() => { this.sessionConflictHandled = false; }, 5000);
      }
    } else if (data.error.statusCode === 404) {
      this.router.navigate(['404']);
      return false;
    } else {
      if (data.error.message && data.error.message !== '') {
        this.toasterService.error('', data.error.message);
      }
    }
    // Note: previously this navigated back with window.history.back() whenever
    // failureResponse(error, true) was called (nearly every call site in the app).
    // That meant any API error — 422/500/401/403/network/CORS — bounced the user
    // back to the previous page instead of letting the current page render with
    // an error toast. Removed so a failed API call no longer forces navigation.
    return this.error;
  }
  customSuccessResponse(data) {
    if (data) {
      this.toasterService.success('', data);
    }
  }
  public getJSON(): Observable<any> {
    return this.http.get('../../../assets/json/state.json');
}
}
